import { TOKEN_KEY } from '@/constants/login'

const SHORT_MAX_AGE = 60 * 60 * 24 // 1 day
const LONG_MAX_AGE = 60 * 60 * 24 * 30 // 30 days

/**
 * 기기 시계 오차 허용치(ms).
 *
 * exp 를 클라이언트 시계와 비교하므로, 시계가 조금 앞선 기기에서 멀쩡한 토큰이
 * 만료로 판정돼 조기 로그아웃되는 것을 막는다. 이보다 큰 오차는 서버 401 이 잡는다.
 * (확신이 없으면 서버 판단에 맡긴다 - getTokenExp 의 파싱 실패 처리와 같은 원칙)
 */
const CLOCK_SKEW_TOLERANCE = 5 * 60 * 1000 // 5 minutes

/**
 * JWT payload 의 exp(초 단위)를 읽는다. 서명 검증은 하지 않는다.
 *
 * JWT 가 아니거나 파싱에 실패하면 null 을 반환한다.
 * 토큰 포맷이 바뀌었을 때 전 사용자가 즉시 로그아웃되는 사고를 막기 위해,
 * 호출부는 null 을 "만료"가 아니라 "알 수 없음"으로 다루고 판단을 서버 401 에 맡긴다.
 */
const getTokenExp = (token?: string | null): number | null => {
  const payload = token?.split('.')[1]
  if (!payload) return null

  try {
    const bytes = Uint8Array.from(atob(payload.replace(/-/g, '+').replace(/_/g, '/')), (c) =>
      c.charCodeAt(0)
    )
    const { exp } = JSON.parse(new TextDecoder().decode(bytes))
    return typeof exp === 'number' ? exp : null
  } catch {
    return null
  }
}

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const nuxtApp = useNuxtApp()
  const isLocal = useRuntimeConfig().public.env === 'local'
  const domain = isLocal ? undefined : '.usimsa.com'
  const sameSite = isLocal ? 'lax' : 'none'

  const userAuth = useCookie(TOKEN_KEY, {
    maxAge: LONG_MAX_AGE,
    secure: !isLocal,
    sameSite,
    domain
  })

  const partnersKey = computed<string>(() => String(router.currentRoute.value.params.partner || ''))

  const isLoginStatus = computed<boolean>(() => {
    if (!userAuth.value) return false

    // exp 를 못 읽으면(JWT 가 아니거나 파싱 실패) 만료로 보지 않고 서버 401 판단에 맡긴다
    const exp = getTokenExp(userAuth.value)
    return exp === null || exp * 1000 + CLOCK_SKEW_TOLERANCE > Date.now()
  })

  const writeUserAuth = (accessToken: string, maxAge: number) => {
    if (!accessToken) return

    // 쿠키만 살아남아 로그인 상태로 오인되지 않도록, 쿠키 수명을
    // "의도한 수명(자동로그인 여부)"과 "토큰 exp" 중 짧은 쪽에 맞춘다.
    // exp 를 못 읽으면 기존처럼 maxAge 로만 만료시킨다.
    const exp = getTokenExp(accessToken)

    // 이미 죽은 토큰을 쓰려는 경로가 있는지 판별용 기록. (앱 브릿지가 오래된 토큰을
    // 재주입하는 케이스가 유력) 동작은 바꾸지 않고 기록만 한다.
    if (exp !== null && exp * 1000 + CLOCK_SKEW_TOLERANCE <= Date.now()) {
      console.warn('[auth] 이미 만료된 토큰을 쿠키에 기록하려 합니다.', {
        expiredAt: new Date(exp * 1000).toISOString(),
        now: new Date().toISOString(),
        isApp: import.meta.client && !!window.flutter_inappwebview
      })
    }
    // 쿠키 만료도 같은 허용치를 쓴다. isLoginStatus 는 유효하다고 보는데 쿠키만
    // 먼저 사라지면 판정이 서로 어긋난다.
    const expires = exp
      ? new Date(Math.min(exp * 1000 + CLOCK_SKEW_TOLERANCE, Date.now() + maxAge * 1000))
      : undefined

    // useCookie 는 호출마다 독립적인 ref 를 돌려준다(같은 탭 안에서 공유되지 않는다).
    // 스토어의 userAuth 를 직접 갱신해야 isLoginStatus 와 `Bearer ${userAuth.value}`
    // 를 쓰는 http 레이어가 즉시 새 토큰을 본다. 이 줄이 없으면 로그인 직후에도
    // Bearer undefined 로 401 이 나고, 그 401 핸들러가 쿠키를 다시 지운다.
    userAuth.value = accessToken

    // 위 쓰기는 스토어 옵션(LONG_MAX_AGE)을 쓰므로, 의도한 수명으로 한 번 더 덮어쓴다.
    // 두 watcher 는 생성 순서대로 flush 되어 아래 쓰기가 마지막에 반영된다.
    const cookie = useCookie(TOKEN_KEY, {
      ...(expires ? { expires } : { maxAge }),
      secure: !isLocal,
      sameSite,
      domain
    })
    cookie.value = accessToken
  }

  const updateShortUserAuth = (accessToken: string) => writeUserAuth(accessToken, SHORT_MAX_AGE)

  const updateLongUserAuth = (accessToken: string) => writeUserAuth(accessToken, LONG_MAX_AGE)

  // 공통 로그아웃 처리
  const logoutUserAuth = async (options?: { redirectToLogin?: boolean }) => {
    userAuth.value = null // 쿠키 제거

    if (options?.redirectToLogin) {
      await navigateTo('/user/login')
    }
  }

  // 401 / 인증실패시
  const handleUnauthorized = () => {
    // /esim/v1/account 등 여러 곳에서 동시에 401 이 날 수 있어 리다이렉트가 겹치지 않게 막는다.
    // useApiClient 의 401 경로와 같은 플래그를 공유한다.
    const { isLogoutInProgress } = storeToRefs(useApiFlagsStore())
    if (isLogoutInProgress.value) return
    isLogoutInProgress.value = true

    logoutUserAuth({ redirectToLogin: false })

    if (import.meta.client) {
      const route = router.currentRoute.value

      // 로그인 페이지에서의 401(예: 비밀번호 오류)은 자기 자신으로 되돌아가므로 이동하지 않는다
      if (!route.path.includes('/user/login')) {
        const partner = route.params.partner
        const returnUrl = encodeURIComponent(route.fullPath)

        nuxtApp.runWithContext(() =>
          navigateTo(
            partner
              ? `/partners/${partner}/user/login?returnUrl=${returnUrl}`
              : `/user/login?returnUrl=${returnUrl}`
          )
        )
      }
    }

    setTimeout(() => {
      isLogoutInProgress.value = false
    }, 2000)
  }

  return {
    isLoginStatus,
    updateShortUserAuth,
    updateLongUserAuth,
    logoutUserAuth,
    handleUnauthorized,
    userAuth,
    partnersKey
  }
})
